import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@hortis/ui/command";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@hortis/ui/dropdown-menu";
import {
  BarChartSquare2,
  Check,
  Map1,
  Settings1,
  SwitchHorizontal1,
  Table,
  Trash3,
} from "@hortis/ui/icons";
import { Skeleton } from "@hortis/ui/skeleton";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { mapSlugToHrefs } from "src/utils/hooks/hrefs";
import { useSettingsReturnToStore } from "../../../features/settings/return-to-store";
import { NavItem } from "../nav-list/nav-item";
import NavGroupHeader from "./nav-group-header";
import { useNavSites } from "./use-nav-sites";

interface SiteNavGroupProps {
  onNavigate?: () => void;
}

const SiteLabel = ({ name, active }: { name: string; active: boolean }) => (
  <div className="flex items-center gap-3">
    <span className="line-clamp-2 ">{name}</span>
    {active && <Check className="h-4 w-4 shrink-0 text-primary-500" />}
  </div>
);

const getCurrentPage = () => {
  const [currentPath] = window.location.pathname.split("/").slice(-1);
  const currentPathQueryParams = window.location.search.split("?")[1] ?? "";
  return { currentPath, currentPathQueryParams };
};

const generateSiteOptionsUrl = (siteUrlSlug: string) => {
  const { currentPath, currentPathQueryParams } = getCurrentPage();
  let redirect: string;
  switch (currentPath) {
    case "dashboard": {
      redirect = mapSlugToHrefs(siteUrlSlug).dashboard;
      break;
    }
    case "collection": {
      const queryParamsMap: Record<string, string> = {
        "tab=materials": mapSlugToHrefs(siteUrlSlug).plantMaterialCollection,
        "tab=accessions": mapSlugToHrefs(siteUrlSlug).accessionCollection,
        "tab=taxa": mapSlugToHrefs(siteUrlSlug).taxaCollection,
      };
      redirect =
        queryParamsMap[currentPathQueryParams] ??
        mapSlugToHrefs(siteUrlSlug).plantMaterialCollection; // default to plant material collection when no query params
      break;
    }
    case "map": {
      redirect = mapSlugToHrefs(siteUrlSlug).map;
      break;
    }
    default: {
      redirect = mapSlugToHrefs(siteUrlSlug).dashboard;
      break;
    }
  }

  return `/${redirect}`;
};

export const SiteNavGroup = ({ onNavigate }: SiteNavGroupProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { setReturnTo } = useSettingsReturnToStore();
  const { activeSite, sites, fetching } = useNavSites();

  const handleItemClick = useCallback(() => {
    setIsOpen(false);
    onNavigate?.();
  }, [onNavigate]);

  if (activeSite == null && fetching) {
    return (
      <div className="flex flex-col gap-1">
        <div className="px-3 py-1 text-xs font-medium text-grey-500">Site</div>
        <div className="flex flex-col gap-px px-3">
          <div className="flex h-9 items-center gap-3">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-4 w-16" />
          </div>
          <div className="flex h-9 items-center gap-3">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex h-9 items-center gap-3">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-4 w-14" />
          </div>
          <div className="flex h-9 items-center gap-3">
            <Skeleton className="h-5 w-5" />
            <Skeleton className="h-4 w-16" />
          </div>
        </div>
      </div>
    );
  }

  if (activeSite == null) {
    return;
  }

  return (
    <div className="flex flex-col gap-1">
      <div className="px-3 py-1 text-xs font-medium text-grey-500">Site</div>
      <nav className="flex flex-col gap-px">
        <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
          <DropdownMenuTrigger className="rounded-lg outline-hidden ring-grey-200/70 focus-visible:ring-3">
            <NavGroupHeader className="px-3 py-2">
              <div className="flex items-center gap-3 overflow-hidden text-left text-sm font-medium">
                <span className="truncate">{activeSite.name}</span>
              </div>
            </NavGroupHeader>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="z-9999 w-[200px]">
            {sites != null && sites.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="flex w-full gap-2 rounded-md px-3 py-2.5 text-sm font-medium">
                  <SwitchHorizontal1 className="h-4 w-4 text-grey-500" />
                  <span>Switch site</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-[200px] p-0">
                  <Command>
                    <CommandInput
                      placeholder="Filter sites..."
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus={true} //unable to manually focus on input with keyboard
                    />
                    <CommandEmpty>No results</CommandEmpty>
                    <CommandList>
                      {sites.map((site) => (
                        <CommandItem
                          key={site.id}
                          value={site.name}
                          onSelect={() => {
                            void navigate({
                              to: generateSiteOptionsUrl(site.urlSlug),
                            });
                            onNavigate?.();
                            setIsOpen(false);
                          }}
                        >
                          <Link to={generateSiteOptionsUrl(site.urlSlug)}>
                            <SiteLabel
                              name={site.name}
                              active={location.pathname.includes(
                                `/${site.urlSlug}/`,
                              )}
                            />
                          </Link>
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link
                to={"/settings/sites/$siteSlug/general"}
                params={{ siteSlug: activeSite.urlSlug }}
                onClick={() => {
                  setReturnTo(location.pathname);
                  handleItemClick();
                }}
                className="flex w-full"
              >
                <Settings1 className="h-4 w-4 text-grey-500" />
                <span className="text-sm">Site settings</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link
                to={"/site/$siteSlug/trash"}
                params={{ siteSlug: activeSite.urlSlug }}
                onClick={() => {
                  setReturnTo(location.pathname);
                  handleItemClick();
                }}
                className="flex w-full"
              >
                <Trash3 className="h-4 w-4 text-grey-500" />
                <span className="text-sm">Site trash</span>
              </Link>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <NavItem asChild>
          <Link
            to="/site/$siteSlug/dashboard"
            params={{ siteSlug: activeSite.urlSlug }}
            onClick={onNavigate}
          >
            <BarChartSquare2 />
            Dashboard
          </Link>
        </NavItem>
        <NavItem asChild>
          <Link
            to="/site/$siteSlug/map"
            params={{ siteSlug: activeSite.urlSlug }}
            onClick={onNavigate}
          >
            <Map1 />
            Map
          </Link>
        </NavItem>
        <NavItem asChild>
          <Link
            to="/site/$siteSlug/collection"
            params={{ siteSlug: activeSite.urlSlug }}
            onClick={onNavigate}
          >
            <Table />
            List
          </Link>
        </NavItem>
      </nav>
    </div>
  );
};
